const WINDOWS_1252_UNICODE_TO_BYTE: Record<number, number> = {
  0x20ac: 0x80,
  0x201a: 0x82,
  0x0192: 0x83,
  0x201e: 0x84,
  0x2026: 0x85,
  0x2020: 0x86,
  0x2021: 0x87,
  0x02c6: 0x88,
  0x2030: 0x89,
  0x0160: 0x8a,
  0x2039: 0x8b,
  0x0152: 0x8c,
  0x017d: 0x8e,
  0x2018: 0x91,
  0x2019: 0x92,
  0x201c: 0x93,
  0x201d: 0x94,
  0x2022: 0x95,
  0x2013: 0x96,
  0x2014: 0x97,
  0x02dc: 0x98,
  0x2122: 0x99,
  0x0161: 0x9a,
  0x203a: 0x9b,
  0x0153: 0x9c,
  0x017e: 0x9e,
  0x0178: 0x9f
};

const GUID_BYTE_ORDER = [3, 2, 1, 0, 5, 4, 7, 6, 8, 9, 10, 11, 12, 13, 14, 15];

const MD5_SHIFT_AMOUNTS = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21
];

const MD5_CONSTANTS = Array.from(
  { length: 64 },
  (_, index) => Math.floor(Math.abs(Math.sin(index + 1)) * 0x100000000) >>> 0
);

const WORKFLOW_DECLARATIONS = `type Guid = string;

declare const debug: (value: unknown) => void;
declare const print: (message: string) => void;
declare const sleep: (ms: number) => Promise<void>;
declare const toGuid: (value: string) => Guid;
`;

const leftRotate = (value: number, amount: number): number =>
  ((value << amount) | (value >>> (32 - amount))) >>> 0;

const toHexByte = (value: number): string => value.toString(16).padStart(2, '0');

const encodeWindows1252 = (value: string): Uint8Array => {
  const bytes: number[] = [];

  for (let index = 0; index < value.length; index++) {
    const codePoint = value.charCodeAt(index);

    if (codePoint <= 0xff) {
      bytes.push(codePoint);
      continue;
    }

    bytes.push(WINDOWS_1252_UNICODE_TO_BYTE[codePoint] ?? 0x3f);
  }

  return Uint8Array.from(bytes);
};

const md5 = (input: Uint8Array): Uint8Array => {
  const originalLength = input.length;
  const paddedLength = (((originalLength + 8) >>> 6) + 1) * 64;
  const buffer = new Uint8Array(paddedLength);
  buffer.set(input);
  buffer[originalLength] = 0x80;

  const bitLengthLow = (originalLength * 8) >>> 0;
  const bitLengthHigh = Math.floor((originalLength * 8) / 0x100000000) >>> 0;
  const lengthView = new DataView(buffer.buffer);
  lengthView.setUint32(paddedLength - 8, bitLengthLow, true);
  lengthView.setUint32(paddedLength - 4, bitLengthHigh, true);

  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;
  const chunkView = new DataView(buffer.buffer);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    const words = new Uint32Array(16);
    for (let wordIndex = 0; wordIndex < 16; wordIndex++) {
      words[wordIndex] = chunkView.getUint32(offset + wordIndex * 4, true);
    }

    let a = a0;
    let b = b0;
    let c = c0;
    let d = d0;

    for (let index = 0; index < 64; index++) {
      let f = 0;
      let g = 0;

      if (index < 16) {
        f = (b & c) | (~b & d);
        g = index;
      } else if (index < 32) {
        f = (d & b) | (~d & c);
        g = (5 * index + 1) % 16;
      } else if (index < 48) {
        f = b ^ c ^ d;
        g = (3 * index + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = (7 * index) % 16;
      }

      const next = d;
      d = c;
      c = b;
      b = (b + leftRotate((a + f + MD5_CONSTANTS[index] + words[g]) >>> 0, MD5_SHIFT_AMOUNTS[index])) >>> 0;
      a = next;
    }

    a0 = (a0 + a) >>> 0;
    b0 = (b0 + b) >>> 0;
    c0 = (c0 + c) >>> 0;
    d0 = (d0 + d) >>> 0;
  }

  const hash = new Uint8Array(16);
  const hashView = new DataView(hash.buffer);
  hashView.setUint32(0, a0, true);
  hashView.setUint32(4, b0, true);
  hashView.setUint32(8, c0, true);
  hashView.setUint32(12, d0, true);
  return hash;
};

const toGuid = (value: string): string => {
  if (typeof value !== 'string') {
    throw new TypeError('toGuid expects a string.');
  }

  const hash = md5(encodeWindows1252(value));
  const ordered = GUID_BYTE_ORDER.map(index => toHexByte(hash[index]));

  return [
    ordered.slice(0, 4).join(''),
    ordered.slice(4, 6).join(''),
    ordered.slice(6, 8).join(''),
    ordered.slice(8, 10).join(''),
    ordered.slice(10, 16).join('')
  ].join('-');
};

const debug = (value: unknown): void => {
  void value;
};

const print = (message: string): void => {
  void message;
};

const sleep = (ms: number): Promise<void> => {
  const delay = Number.isFinite(ms) ? Math.max(0, Math.trunc(ms)) : 0;
  return new Promise(resolve => {
    globalThis.setTimeout(resolve, delay);
  });
};

export const workflowScriptEnvironment = {
  declarations: WORKFLOW_DECLARATIONS,
  declarationsPath: 'ts:workflow-editor/workflow-script-builtins.d.ts',
  globals: {
    debug,
    print,
    sleep,
    toGuid
  },
  prelude: `const { debug, print, sleep, toGuid } = __context.environmentGlobals;`
} as const;
